// composables/useChatMessages.js
import { reactive } from 'vue'
import { ref, nextTick } from 'vue'
import { lockStaleInteractions } from '../utils/interactions'

const toolLabels = {
  query_nps: 'Consultando base de datos...',
  query_count: 'Consultando conteo...',
  query_comparative: 'Realizando analisis comparativo',
  query_knowledge: 'Buscando informacion...',
  generate_chart: 'Generando grafico...',
  get_available_metrics: 'Buscando metricas...',
  get_available_questionaries: 'Buscando cuestionarios...',
  get_filters: 'Consultando filtros',
  get_special_filters: 'Consultando filtros especiales',
  get_conversation_history: 'Consultando historial de conversacion...',
  resolve_filters: 'Resolviendo filtros',
  query_industry_benchmark: 'Buscando en la web...',
  query_comments: 'Consultando comentarios',
  get_goals: 'Consultando metas',
  save_goal: 'Guardando meta',
  complete_onboarding: 'Onboarding completado',
  get_onboarding: 'Completando onboarding',
  manage_cron: 'Gestionando tareas programadas...',
  build_presentation: 'Generando presentacion',
  manage_knowledge: "Gestionando conocimiento",
}

const ERROR_TEXT = 'Oh Oh, lo sentimos hubo un error vuelve a intentarlo por favor, si el error persiste no dudes en contactarnos, estamos para ayudarte.'

export function useChatMessages(scrollToBottom, rootEl) {
  const apiUrl = process.env.VUE_APP_API_URL
  const messages = ref([])
  const isLoading = ref(false)

  // Consume the SSE stream of a chat turn. Shared by a normal message and an interactive answer,
  // since answering uses the same contract (text_delta … done).
  const processStream = async ({ res, messages, setLoading, scrollToBottom }) => {
    let assistantMsg = null
    let assistantStarted = false

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // SSE blocks are separated by \n\n
      const blocks = buffer.split('\n\n')
      buffer = blocks.pop() ?? ''

      for (const block of blocks) {
        let eventType = ''
        let dataLine = ''

        for (const line of block.split('\n')) {
          if (line.startsWith('event:')) eventType = line.slice(6).trim()
          if (line.startsWith('data:')) dataLine = line.slice(5).trim()
        }

        if (!dataLine) continue
        let parsed
        try { parsed = JSON.parse(dataLine) } catch { continue }

        switch (eventType) {
          case 'tool_start': {
            messages.push({
              sender: 'assistant',
              messageType: 'status',
              status: toolLabels[parsed.name] || 'Procesando información',
              toolId: parsed.id
            })
            scrollToBottom()
            break
          }
          case 'tool_done': {
            const statusMsg = messages.find(
              m => m.messageType === 'status' && m.toolId === parsed.id
            )

            if (statusMsg) {
              statusMsg.completed = true
              statusMsg.status = `✓ ${toolLabels[parsed.name] || 'Procesando información'}`
            }
            break
          }
          case 'text_delta':
            if (!assistantStarted) {
              assistantMsg = reactive({
                sender: 'assistant',
                messageText: '',
                messageType: 'text',
                goodResponse: null,
                createdAt: new Date().toISOString()
              })

              messages.push(assistantMsg)
              assistantStarted = true
            }
            assistantMsg.messageText += parsed.delta
            break
          case 'interactive':
            // A structured prompt (question / confirm / form / prompt) the user can answer inline.
            // Also the wizard "next step" event — it supersedes any earlier live prompt.
            messages.push(reactive({
              sender: 'assistant',
              messageText: '',
              messageType: 'interactive',
              payload: parsed,
              answered: false,
              superseded: false,
              answerSummary: '',
              createdAt: new Date().toISOString()
            }))
            lockStaleInteractions(messages)
            scrollToBottom()
            break
          case 'document':
            messages.push({
              sender: 'assistant',
              messageText: parsed.messageText || '',
              messageType: 'document',
              url: parsed.url,
              chartUrl: parsed.chartUrl,
              fileUrl: parsed.fileUrl,
              goodResponse: null,
              createdAt: new Date().toISOString()
            })
            scrollToBottom()
            break
          case 'done': {
            const statusMsg = messages.find(
              m => m.messageType === 'status' && m.toolId === parsed.id
            )

            if (statusMsg) {
              statusMsg.completed = true
              statusMsg.status = `✓ ${parsed.name} completado`
            }
            setLoading(false)
            break
          }
          case 'error':
            if (assistantMsg) assistantMsg.messageText = ERROR_TEXT
            else messages.push({ sender: 'assistant', messageText: ERROR_TEXT, goodResponse: null, messageType: 'text', createdAt: new Date().toISOString() })
            setLoading(false)
            break
        }
      }
    }

    setLoading(false)
  }

  // POST to /chat-messages and stream the reply. `body` differs for a plain message vs an answer.
  const postAndStream = async ({ body, apiKey, messages, setLoading, scrollToBottom }) => {
    try {
      const res = await fetch(`${apiUrl}/chat-messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apiKey': apiKey },
        body: JSON.stringify(body)
      })

      if (res.status === 429) {
        messages.push({ sender: 'assistant', messageText: 'Superaste el limite de peticiones permitidas para tu usuario este mes, contactanos si crees que es un error, estamos para ayudarte', goodResponse: null, messageType: 'text', createdAt: new Date().toISOString() })
        setLoading(false)
        return
      }

      if (!res.ok || !res.body) {
        messages.push({ sender: 'assistant', messageText: ERROR_TEXT, goodResponse: null, messageType: 'text', createdAt: new Date().toISOString() })
        setLoading(false)
        return
      }

      await processStream({ res, messages, setLoading, scrollToBottom })
    } catch (error) {
      setLoading(false)
      console.error(error)
      messages.push({ sender: 'assistant', messageText: ERROR_TEXT, goodResponse: null, messageType: 'text', createdAt: new Date().toISOString() })
    }
  }

  const sendMessage = async ({ input, sessionId, apiKey, messages, setInput, setLoading, scrollToBottom, platform }) => {
    const text = input.trim()
    if (!text) return

    setLoading(true)
    messages.push({ sender: 'user', messageText: text, messageType: 'text', createdAt: new Date().toISOString() })
    setInput('')
    scrollToBottom()

    await postAndStream({
      body: { sessionId, sender: 'user', messageText: text, messageType: 'text', platform },
      apiKey,
      messages,
      setLoading,
      scrollToBottom
    })
  }

  // Answer an interactive message. Same endpoint + SSE contract as a normal turn.
  // Only sessionId/interactionId/answer are sent — sender, messageType and messageText are inferred.
  const sendAnswer = async ({ sessionId, apiKey, interactionId, answer, messages, setLoading, scrollToBottom }) => {
    setLoading(true)
    scrollToBottom()

    await postAndStream({
      body: { sessionId, interactionId, answer },
      apiKey,
      messages,
      setLoading,
      scrollToBottom
    })
  }

  // Invoke a "/" palette command. Dispatch is deterministic server-side; only sessionId + command
  // are sent. The reply uses the normal SSE contract (usually ending with an `interactive` step).
  const sendCommand = async ({ sessionId, apiKey, command, label, messages, setLoading, scrollToBottom }) => {
    setLoading(true)
    // Show the invocation locally; the backend records its own version as the user's message.
    messages.push({ sender: 'user', messageText: label || command, messageType: 'text', createdAt: new Date().toISOString() })
    scrollToBottom()

    await postAndStream({
      body: { sessionId, command },
      apiKey,
      messages,
      setLoading,
      scrollToBottom
    })
  }

  const setFeedback = async (msg, value, apiKey) => {
    if (msg.goodResponse === value) return
    msg.goodResponse = value
    msg.rating = msg.goodResponse ? 5 : 1
    try {
      const res = await fetch(`${apiUrl}/chat-messages/${msg.id}/rating`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apiKey': apiKey},
        body: JSON.stringify({
          "rating": msg.goodResponse ? 5 : 1
        })
      })
    } catch (error) {
      console.log(error)
    }
  }

  const donwloadFile = async (url, apiKey) => {
    try {
      const res = await fetch(`${apiUrl}/chat-messages/download-file`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apiKey': apiKey},
        body: JSON.stringify({
          "url": url
        })
      })
      if (!res.ok) throw new Error('Failed to download file');

      // Get the file as a blob
      const blob = await res.blob();

      const filename = url.split('/').pop() || 'downloaded-file';

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.log(error)
    }
  }

  return { messages, isLoading, sendMessage, sendAnswer, sendCommand, setFeedback, donwloadFile }
}
